// ── Slow Moving Inventory tab ───────────────────────────────────────────────
// Shared by the certificate page and the Borrower Dashboard so the tab is the
// same on both, like A/R, A/P and Inventory (collateralCards.js). Moved here
// verbatim from BorrowingBaseDetail; only the page wiring changed:
//   cur      the certificate in view (id, asofdate) — the open one on the
//            certificate page, the latest with a slow-moving sheet on the dashboard
//   smLines  that certificate's SKU lines (getBorrowingBaseSlowMoving); null = loading
//   skus     every certificate's per-SKU roll-up (getCollateralPartyTrend .skus)
//   onOpen   open another certificate; editing disables row clicks
import { useState } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate, fmtRatio } from "../../shared/format";
import { Card, Spinner, Td, TdNum, FInput, Pagination } from "../../shared/ui";
import { BarTrend, TH_WRAP } from "./trendCharts";
import { SkuAnalysis } from "./collateralCards";

const PARTY_COLORS = ["#12b5db", "#ffb20d", "#0F6E56", "#993C1D", "#6a4c93", "#e07c1a", "#116588", "#c15418", "#8a5a00", "#5A8F3C"];
const iso = d => d ? String(d).slice(0, 10) : "";
const fmtShort = v => { const a = Math.abs(v); return (a >= 1e6 ? (v / 1e6).toFixed(1) + "M" : a >= 1e3 ? Math.round(v / 1e3) + "K" : String(Math.round(v))); };
const SMSER = [
  { id: "cost", label: "Inventory at cost", key: "sm_total_cost", color: "#A5E3F1", w: 0.72 },
  { id: "ttm", label: "Cost > TTM", key: "sm_xs_cost_ttm", color: "#12b5db", w: 0.48 },
  { id: "t24m", label: "Cost > 24M", key: "sm_xs_cost_t24m", color: "#116588", w: 0.26 },
];

export const SlowMovingTab = ({ cur, smLines, skus, onOpen, editing = false }) => {
  const [smQ, setSmQ] = useState("");
  const [smIncOnly, setSmIncOnly] = useState(false);
  const [smTrendOn, setSmTrendOn] = useState({ cost: true, ttm: true, t24m: true });
  const [smTrendIncOnly, setSmTrendIncOnly] = useState(false);
  const [smPage, setSmPage] = useState(1);
  // By SKU sort. Null key = the borrower's sheet order.
  const [smSort, setSmSort] = useState({ key: null, dir: "desc" });
  const [topSkuOff, setTopSkuOff] = useState({});
  const [topSkuXsOff, setTopSkuXsOff] = useState({});

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const hCell = { padding: "8px 12px", fontSize: 12, fontWeight: 700, color: B.cyanDark, background: B.cyanLight, borderBottom: "1px solid " + B.cyan, whiteSpace: "nowrap" };
  const smTot = { fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" };
  const thSticky = { fontSize: 12, fontWeight: 600, color: B.cyanDark, background: "#ffffff", ...TH_WRAP, position: "sticky", top: 0, zIndex: 3, borderBottom: "2px solid " + B.cyan, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" };

  const smAll = smLines || [];
  // "Included" is the borrower's own call, normalized at parse time from either
  // an Include? column or an inverted Exclude column. Rows where they said
  // nothing (null) are kept — silence is not exclusion.
  const smRows = (smIncOnly ? smAll.filter(l => l.include_flag !== false) : smAll)
    .filter(l => {
      const q = smQ.trim().toLowerCase();
      if (!q) return true;
      return String(l.unique_id || "").toLowerCase().includes(q)
          || String(l.description || "").toLowerCase().includes(q);
    });
  // Sorted across ALL matching rows before paging, so page 1 really is the top.
  // Blanks always sort last. Numbers open largest-first; text A→Z.
  const SM_TEXT = { unique_id: 1, description: 1 };
  const smSortVal = (l, k) => {
    if (k === "include_flag") return l.include_flag === false ? 0 : l.include_flag === true ? 2 : 1;
    const v = l[k];
    if (v == null || v === "") return null;
    if (k === "prod_start_date") return Date.parse(String(v).slice(0, 10)) || null;
    if (SM_TEXT[k]) return String(v).toLowerCase();
    const n = parseFloat(v); return isFinite(n) ? n : null;
  };
  if (smSort.key) {
    const k = smSort.key, m = smSort.dir === "asc" ? 1 : -1;
    smRows.sort((a, b) => {
      const av = smSortVal(a, k), bv = smSortVal(b, k);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (typeof av === "string" ? av.localeCompare(bv, undefined, { numeric: true }) : av - bv) * m;
    });
  }
  const smSortBy = k => {
    setSmPage(1);
    setSmSort(s => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: SM_TEXT[k] ? "asc" : "desc" });
  };
  const smTh = (k, label, right) => (
    <th key={k} onClick={() => smSortBy(k)} style={{ ...hCell, textAlign: right ? "right" : "left", cursor: "pointer", userSelect: "none" }}
        title="Click to sort">
      {label}<span style={{ fontSize: 10, marginLeft: 4, opacity: smSort.key === k ? 1 : 0.3 }}>
        {smSort.key === k ? (smSort.dir === "asc" ? "▲" : "▼") : "▼"}</span>
    </th>
  );
  const smSum = k => smRows.reduce((t, l) => t + (parseFloat(l[k]) || 0), 0);
  // Paged rather than capped. The table used to render the first 500 and simply
  // stop: the remainder counted toward the totals but could not be reached, so a
  // SKU sorted below 500th was unviewable.
  //
  // Derived, not a hook — usePagination holds state, and this sits below the
  // loading/error early returns where a hook cannot go. The page number itself
  // is useState at the top of the component. Clamped so a stale page number can
  // never render an empty table.
  const SM_PAGE = 100;
  const smTotalPages = Math.max(1, Math.ceil(smRows.length / SM_PAGE));
  const smPageNow = Math.min(smPage, smTotalPages);
  const smPaged = smRows.slice((smPageNow - 1) * SM_PAGE, smPageNow * SM_PAGE);
  // Whether the borrower reported these at all. A column nobody supplied is
  // hidden rather than shown as a wall of dashes that reads like a broken parse.
  const smHas = k => smAll.some(l => l[k] !== null && l[k] !== undefined);
  const smHasT24M = smHas("xs_cost_t24m") || smHas("t24m_units_sold");
  const smHasProdDate = smHas("prod_start_date");
  const buildParty = (rows, nameKey = "consolidatedname", valKey = "open_balance", altKey = null) => {
    const byDate = {}; const altByDate = {}; const dset = new Set();
    (rows || []).forEach(r => { const d = iso(r.asofdate); if (!d) return; dset.add(d); (byDate[d] = byDate[d] || {}); byDate[d][r[nameKey]] = (byDate[d][r[nameKey]] || 0) + (parseFloat(r[valKey]) || 0);
      if (altKey) { (altByDate[d] = altByDate[d] || {}); altByDate[d][r[nameKey]] = (altByDate[d][r[nameKey]] || 0) + (parseFloat(r[altKey]) || 0); } });
    const dates = [...dset].sort();                    // ascending (oldest → newest)
    const latest = dates[dates.length - 1];
    const top = latest ? Object.entries(byDate[latest]).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([n]) => n) : [];
    return { dates, byDate, top, altByDate };
  };
  const smTrendSkus = (skus || [])
    .filter(r => !smTrendIncOnly || r.include_flag !== 0);
  // ── Top-10 SKU series ──
  // Always on the INCLUDED basis, regardless of the trend toggle above. Ranking
  // SKUs the borrower has told us not to count would put items at the top of a
  // watch-list that nobody intends to act on. The cards say so on their face.
  const smTopSkus = (skus || []).filter(r => r.include_flag !== 0);
  // Ranked on units on hand, with cost carried alongside: a count on its own
  // does not say whether the pile matters, and a cheap part in huge quantity is
  // a different conversation from an expensive one in small.
  const skuTrendUnits = buildParty(smTopSkus, "unique_id", "unit_count", "total_cost");
  // Ranked on excess over TTM — the overstock itself. Watching this list turn
  // over, or fail to, is how you tell whether they are actually working it down.
  const skuTrendXs = buildParty(smTopSkus, "unique_id", "xs_cost_ttm");
  // Both SKU cards show the same three figures on every tile — units on hand,
  // cost, and cost above TTM sales — so a SKU on both lists reads identically.
  // The cards differ only in which figure they rank by (shown large, with the
  // change vs the prior BBC).
  const skuFigs = {};
  smTopSkus.forEach(r => {
    const d = iso(r.asofdate); if (!d) return;
    const e = ((skuFigs[d] = skuFigs[d] || {})[r.unique_id] = skuFigs[d][r.unique_id] || { units: 0, cost: 0, xs: 0 });
    e.units += parseFloat(r.unit_count) || 0; e.cost += parseFloat(r.total_cost) || 0; e.xs += parseFloat(r.xs_cost_ttm) || 0;
  });
  const SKU_FIGS = [
    { k: "units", label: "On hand" },
    { k: "cost",  label: "At cost" },
    { k: "xs",    label: "Above TTM" },
  ];
  const smUnits = v => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const smIncludedNote = "Ranked across the SKUs the borrower marks as included — the same basis as their own totals. Excluded SKUs never appear here, whatever the trend toggle above is set to.";
  // Period-over-period roll-ups, oldest first.
  //
  // Aggregated from the per-SKU rows rather than read from the stored header
  // totals. The header roll-ups are fixed at "included only" — the basis the
  // borrower's own totals use — and cannot answer the other question. Summing
  // the detail lets the same toggle drive the cards, the chart and the table,
  // so all three always describe the same set of rows.
  //
  // Only certificates that actually carried a slow-moving sheet appear: a
  // borrower who reported it twice and then stopped shows two points and a gap,
  // not three with a fabricated zero.
  const smHistory = (() => {
    const by = {};
    for (const r of smTrendSkus) {
      const d = iso(r.asofdate); if (!d) continue;
      const e = by[r.bbid] = by[r.bbid] || {
        id: r.bbid, asofdate: r.asofdate, _d: d,
        sm_sku_count: 0, sm_total_cost: 0, sm_xs_cost_ttm: 0, sm_xs_cost_t24m: 0,
      };
      e.sm_sku_count += 1;
      e.sm_total_cost += parseFloat(r.total_cost) || 0;
      e.sm_xs_cost_ttm += parseFloat(r.xs_cost_ttm) || 0;
      e.sm_xs_cost_t24m += parseFloat(r.xs_cost_t24m) || 0;
    }
    return Object.values(by).sort((a, b) => a._d.localeCompare(b._d));
  })();

  const partyTrendCard = (title, td, offState, setOffState, grandLabel, opts = {}) => {
    const fmtVal = opts.fmtVal || fmt;
    // Debtors and Vendors need two periods — a one-point trend line says
    // nothing they do not already show elsewhere on the page. The SKU cards pass
    // minPeriods: 1, because the RANKING is useful from the first certificate
    // even when there is nothing to trend yet; the delta badge is simply omitted
    // until there is a prior period to compare with.
    if (!td || td.dates.length < (opts.minPeriods || 2) || td.top.length === 0) return null;
    const asc = td.dates;   // ascending
    const val = (d, n) => td.byDate[d]?.[n] || 0;
    const grandOf = d => Object.values(td.byDate[d] || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const gmax = Math.max(1, ...asc.flatMap(d => td.top.map(nm => val(d, nm))));
    return (
      <Card style={{ overflowX: "auto", opacity: editing ? 0.6 : 1 }}>
        <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>{title} <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>(top {td.top.length} as of {fmtDate(asc[asc.length - 1])} · {asc.length} periods · Δ vs prior BBC)</span></div>
        {opts.note && <div style={{ padding: "0 12px 8px", fontSize: 12, color: "var(--color-text-secondary)" }}>{opts.note}</div>}
        <div style={{ padding: "10px 12px 6px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
            {td.top.map(nm => {
              const vals = asc.map(d => val(d, nm));
              const last = vals[vals.length - 1];
              // No prior period means no change to report. Without this the
              // badge renders "NaN%" on a first certificate.
              const hasPrev = vals.length > 1;
              const prev = hasPrev ? vals[vals.length - 2] : null;
              const dlt = hasPrev ? last - prev : 0, pct = (hasPrev && prev) ? (dlt / prev * 100) : 0, flat = Math.abs(pct) < 1;
              const col = flat ? "var(--color-text-secondary)" : (dlt > 0 ? "#993C1D" : "#0F6E56");
              const bg = flat ? "#eef1f4" : (dlt > 0 ? "#FBECE7" : "#E1F5EE");
              const SW = 168, SH = 46, sp = 5;
              const sx = i => sp + (SW - 2 * sp) * (vals.length > 1 ? i / (vals.length - 1) : 0.5);
              const sy = v => SH - sp - (SH - 2 * sp) * (v / gmax);
              const pts = vals.map((v, i) => sx(i) + "," + sy(v)).join(" ");
              return (
                <div key={nm} style={{ border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nm}>{nm}</div>
                  <svg width="100%" viewBox={`0 0 ${SW} ${SH}`} style={{ display: "block", margin: "4px 0" }}>
                    <polygon points={`${sp},${SH - sp} ${pts} ${SW - sp},${SH - sp}`} fill={B.cyan} opacity="0.08" />
                    <polyline points={pts} fill="none" stroke={B.cyan} strokeWidth="1.6" />
                    <circle cx={sx(vals.length - 1)} cy={sy(last)} r="2.8" fill={flat ? B.cyanDark : col} />
                  </svg>
                  <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtVal(last)}
                    {opts.valLabel && <span style={{ fontSize: 11, fontWeight: 400, color: "var(--color-text-secondary)", marginLeft: 5 }}>{opts.valLabel}</span>}</div>
                  {opts.figKey && (() => {
                    const fg = skuFigs[asc[asc.length - 1]]?.[nm] || { units: 0, cost: 0, xs: 0 };
                    return <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums", lineHeight: 1.45 }}>
                      {SKU_FIGS.filter(x => x.k !== opts.figKey).map(x => (
                        <div key={x.k} style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                          <span>{x.label}</span><span>{x.k === "units" ? smUnits(fg.units) : fmt(fg[x.k])}</span>
                        </div>))}
                    </div>;
                  })()}
                  {opts.altOf && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
                    {opts.altLabel} {fmt(opts.altOf(asc[asc.length - 1], nm))}
                  </div>}
                  {hasPrev && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 10, display: "inline-block", marginTop: 3, color: col, background: bg }}>
                    {flat ? "–" : (dlt > 0 ? "▲" : "▼")} {dlt >= 0 ? "+" : "−"}{fmtShort(Math.abs(dlt))} · {dlt >= 0 ? "+" : "−"}{Math.abs(pct).toFixed(0)}%
                  </span>}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
            <thead><tr>
              <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
              {td.top.map((nm, i) => <th key={nm} style={{ ...thSticky, textAlign: "right" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: PARTY_COLORS[i % PARTY_COLORS.length], display: "inline-block" }} />{nm}</span></th>)}
              <th style={{ ...thSticky, textAlign: "right" }}>Top {td.top.length} Total</th>
              <th style={{ ...thSticky, textAlign: "right" }}>{grandLabel}</th>
            </tr></thead>
            <tbody>
              {asc.slice().reverse().map(d => {
                const tot = td.top.reduce((s, nm) => s + val(d, nm), 0);
                const grand = grandOf(d);
                const pctSpan = v => <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({grand ? (v / grand * 100).toFixed(1) : "0.0"}%)</span>;
                return (
                  <tr key={d} style={{ background: iso(cur.asofdate) === d ? B.cyanLight : "" }}>
                    <Td style={{ fontWeight: iso(cur.asofdate) === d ? 600 : 400 }}>{fmtDate(d)}</Td>
                    {td.top.map(nm => { const v = val(d, nm); return <TdNum key={nm}>{fmtVal(v)}{pctSpan(v)}</TdNum>; })}
                    <TdNum style={{ fontWeight: 600 }}>{fmtVal(tot)}{pctSpan(tot)}</TdNum>
                    <TdNum style={{ fontWeight: 600 }}>{fmtVal(grand)}</TdNum>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {smLines === null
          ? <Spinner />
          : <>
          {/* Roll-ups reflect the CURRENT filter, so they always agree with the
              table beneath them. Filter to a product line and the exposure
              figures follow — otherwise the totals silently describe a
              different set of rows than the one on screen. */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Card style={{ padding: 16, flex: "1 1 200px", minWidth: 180 }}>
              <div style={cardTitle}>SKUs</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{smRows.length.toLocaleString()}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
                {smRows.length !== smAll.length
                  ? `${(smAll.length - smRows.length).toLocaleString()} hidden by filters`
                  : `all reported SKUs`}
              </div>
            </Card>
            <Card style={{ padding: 16, flex: "1 1 200px", minWidth: 180 }}>
              <div style={cardTitle}>Inventory at cost</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{fmt(smSum("total_cost"))}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
                {smSum("unit_count").toLocaleString()} units on hand
              </div>
            </Card>
            <Card style={{ padding: 16, flex: "1 1 200px", minWidth: 180 }}>
              <div style={cardTitle}>Cost above TTM sales</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#12b5db" }}>{fmt(smSum("xs_cost_ttm"))}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
                {smSum("total_cost") > 0
                  ? fmtRatio(smSum("xs_cost_ttm") / smSum("total_cost")) + " of cost"
                  : "—"}
              </div>
            </Card>
            {smHasT24M && <Card style={{ padding: 16, flex: "1 1 200px", minWidth: 180 }}>
              <div style={cardTitle}>Cost above 24-month sales</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#116588" }}>{fmt(smSum("xs_cost_t24m"))}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
                the deeper bucket
              </div>
            </Card>}
          </div>

          {/* Appraisal-style SKU views: demand, change components, top movers, COGS turnover */}
          <SkuAnalysis skus={skus} curId={cur.id} curAsof={cur.asofdate} />

          {/* ── Slow-Moving Trend ──
              Same construction as the A/R Trend: nested-subset bars oldest to
              newest with toggleable series, then the figures beneath, newest
              first, with the certificate you are on highlighted and every row
              clickable. */}
          {smHistory.length > 1 && (
            <Card>
              <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0, display: "flex", alignItems: "center", gap: 12 }}>
                <span>Slow-Moving Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({smHistory.length})</span></span>
                <div style={{ flex: 1 }} />
                {/* Governs the chart, the table AND the Top 10 SKUs below, so
                    every figure on the tab is on one basis at a time. Off by
                    default: show everything the borrower reported. */}
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 400,
                                textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap", cursor: "pointer",
                                color: "var(--color-text-secondary)" }}
                       title="Narrow the trend to the SKUs the borrower marked as included — the same basis as their own totals.">
                  <input type="checkbox" checked={smTrendIncOnly} onChange={e => setSmTrendIncOnly(e.target.checked)}
                         style={{ width: 14, height: 14, cursor: "pointer" }} />
                  Included only
                </label>
              </div>

              <div style={{ padding: "8px 12px 4px" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  {SMSER.filter(x => x.id !== "t24m" || smHasT24M).map(x => (
                    <button key={x.id} onClick={() => setSmTrendOn(o => ({ ...o, [x.id]: !o[x.id] }))}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: smTrendOn[x.id] ? 1 : 0.4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: x.color, display: "inline-block" }} />
                      {x.label}
                    </button>
                  ))}
                </div>
                <BarTrend rows={smHistory} series={SMSER.filter(x => smTrendOn[x.id] && (x.id !== "t24m" || smHasT24M))} />
              </div>

              <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                  <thead><tr>
                    <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                    <th style={{ ...thSticky, textAlign: "right" }}>SKUs</th>
                    <th style={{ ...thSticky, textAlign: "right" }}>Inventory at cost</th>
                    <th style={{ ...thSticky, textAlign: "right" }}>Cost &gt; TTM</th>
                    <th style={{ ...thSticky, textAlign: "right" }}>% of cost</th>
                    {smHasT24M && <th style={{ ...thSticky, textAlign: "right" }}>Cost &gt; 24M</th>}
                    <th style={{ ...thSticky, textAlign: "right" }}>Δ Cost &gt; TTM</th>
                  </tr></thead>
                  <tbody>
                    {/* Newest first, like the A/R trend. The deltas were computed
                        oldest-first, so they are read from the reversed copy by id
                        rather than by index. */}
                    {smHistory.slice().reverse().map(r => {
                      const tc = parseFloat(r.sm_total_cost) || 0;
                      const xs = parseFloat(r.sm_xs_cost_ttm) || 0;
                      const i = smHistory.findIndex(z => z.id === r.id);
                      const prev = i > 0 ? (parseFloat(smHistory[i - 1].sm_xs_cost_ttm) || 0) : null;
                      const delta = prev == null ? null : xs - prev;
                      return (
                        <tr key={r.id}
                            style={{ cursor: editing ? "default" : "pointer", background: r.id === cur.id ? B.cyanLight : "" }}
                            onClick={() => { if (!editing) onOpen(r.id); }}>
                          <Td style={{ fontWeight: r.id === cur.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                          <TdNum>{r.sm_sku_count == null ? "—" : Number(r.sm_sku_count).toLocaleString()}</TdNum>
                          <TdNum>{fmt(tc)}</TdNum>
                          <TdNum>{fmt(xs)}</TdNum>
                          <TdNum>{tc > 0 ? fmtRatio(xs / tc) : "—"}</TdNum>
                          {smHasT24M && <TdNum>{r.sm_xs_cost_t24m == null ? "—" : fmt(r.sm_xs_cost_t24m)}</TdNum>}
                          {/* Rising excess is the bad direction; it is the one coloured. */}
                          <TdNum style={{ color: delta == null ? undefined : (delta > 0 ? "#993C1D" : delta < 0 ? "#0F6E56" : undefined) }}>
                            {delta == null ? "—" : (delta > 0 ? "+" : "") + fmt(delta)}
                          </TdNum>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── Top 10 by units on hand ──
              What is physically piling up. Cost sits under each count so a
              cheap part in huge quantity is not mistaken for a problem the size
              of an expensive one. */}
          {partyTrendCard(
            "Top 10 SKUs — Units on Hand", skuTrendUnits, topSkuOff, setTopSkuOff,
            "Total units (all SKUs)",
            { fmtVal: smUnits, figKey: "units", valLabel: "units on hand",
              note: smIncludedNote, minPeriods: 1 })}

          {/* ── Top 10 by excess over TTM ──
              The overstock itself, and the reason this section exists: whether
              the same SKUs sit at the top period after period, or the list turns
              over and shrinks. */}
          {partyTrendCard(
            "Top 10 SKUs — Cost above TTM sales", skuTrendXs, topSkuXsOff, setTopSkuXsOff,
            "Total excess (all SKUs)",
            { figKey: "xs", valLabel: "above TTM sales", note: smIncludedNote + " A list that keeps the same names and the same figures period after period is inventory that is not being worked down.",
              minPeriods: 1 })}

          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <span style={{ ...cardTitle, marginBottom: 0 }}>By SKU</span>
              <div style={{ flex: 1 }} />
              <FInput value={smQ} onChange={setSmQ} placeholder="Filter SKU or description…" />
              {/* The borrower's own inclusion call. Shown as a toggle rather
                  than applied silently, because "why does this total not match
                  their spreadsheet" is otherwise unanswerable from the screen. */}
              {/* Off by default: everything the borrower reported is shown, and
                  the Included column says what they made of each row. Tick to
                  narrow to the rows their own totals are built from. */}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer" }}
                     title="Narrow to the rows the borrower marked as included — the same basis as their own totals.">
                <input type="checkbox" checked={smIncOnly} onChange={e => setSmIncOnly(e.target.checked)}
                       style={{ width: 15, height: 15, cursor: "pointer" }} />
                Included only
              </label>
            </div>

            {smRows.length === 0
              ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
                  {smAll.length ? "No SKUs match the current filter." : "No slow-moving inventory reported."}
                </div>
              : <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  {/* Totals ABOVE the columns, as on the Debtors and Vendors
                      tables. They belong at the top: the answer to "how much
                      slow-moving inventory is there" should not require
                      scrolling past several hundred SKUs to reach. The figures
                      follow the search and the Included filter, so they always
                      describe the rows on screen. */}
                  <thead>
                    <tr style={{ background: "#FDF0D5" }}>
                      {/* The totals span every matching SKU, not just this page. */}
                      <Td style={smTot}>Total ({smRows.length.toLocaleString()})</Td>
                      <Td style={smTot}></Td>
                      <Td style={smTot}>{smRows.filter(l => l.include_flag === false).length
                        ? `${smRows.filter(l => l.include_flag !== false).length.toLocaleString()} incl.` : ""}</Td>
                      {smHasProdDate && <Td style={smTot}></Td>}
                      <TdNum style={smTot}>{smSum("unit_count").toLocaleString()}</TdNum>
                      <TdNum style={smTot}></TdNum>
                      <TdNum style={smTot}>{fmt(smSum("total_cost"))}</TdNum>
                      <TdNum style={smTot}>{smSum("ttm_units_sold").toLocaleString()}</TdNum>
                      <TdNum style={smTot}>{smSum("xs_units_ttm").toLocaleString()}</TdNum>
                      <TdNum style={smTot}>{fmt(smSum("xs_cost_ttm"))}</TdNum>
                      {smHasT24M && <TdNum style={smTot}>{smSum("xs_units_t24m").toLocaleString()}</TdNum>}
                      {smHasT24M && <TdNum style={smTot}>{fmt(smSum("xs_cost_t24m"))}</TdNum>}
                    </tr>
                    <tr>
                      {smTh("unique_id", "SKU")}
                      {smTh("description", "Description")}
                      {smTh("include_flag", "Included")}
                      {smHasProdDate && smTh("prod_start_date", "Prod. start")}
                      {smTh("unit_count", "On hand", true)}
                      {smTh("unit_cost", "Unit cost", true)}
                      {smTh("total_cost", "Total cost", true)}
                      {smTh("ttm_units_sold", "TTM sold", true)}
                      {smTh("xs_units_ttm", "Units > TTM", true)}
                      {smTh("xs_cost_ttm", "Cost > TTM", true)}
                      {smHasT24M && smTh("xs_units_t24m", "Units > 24M", true)}
                      {smHasT24M && smTh("xs_cost_t24m", "Cost > 24M", true)}
                    </tr>
                  </thead>
                  <tbody>
                    {smPaged.map((l, i) => (
                      <tr key={(l.unique_id || "") + "-" + i}
                          style={{ opacity: l.include_flag === false ? 0.5 : 1 }}
                          title={l.include_flag === false ? "Borrower flagged this SKU as excluded" : undefined}>
                        <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{l.unique_id || "—"}</Td>
                        <Td style={{ fontSize: 12, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={l.description || ""}>{l.description || "—"}</Td>
                        <Td style={{ fontSize: 12 }}>
                          {l.include_flag === false
                            ? <span style={{ color: "#993C1D", fontWeight: 600 }}>No</span>
                            : l.include_flag === true
                              ? <span style={{ color: "#0F6E56" }}>Yes</span>
                              : <span style={{ color: "var(--color-text-secondary)" }} title="The borrower did not mark this SKU either way">—</span>}
                        </Td>
                        {smHasProdDate && <Td style={{ fontSize: 12 }}>{l.prod_start_date ? fmtDate(l.prod_start_date) : "—"}</Td>}
                        <TdNum>{l.unit_count == null ? "—" : Number(l.unit_count).toLocaleString()}</TdNum>
                        <TdNum>{l.unit_cost == null ? "—" : fmt(l.unit_cost)}</TdNum>
                        <TdNum>{l.total_cost == null ? "—" : fmt(l.total_cost)}</TdNum>
                        <TdNum>{l.ttm_units_sold == null ? "—" : Number(l.ttm_units_sold).toLocaleString()}</TdNum>
                        <TdNum>{l.xs_units_ttm == null ? "—" : Number(l.xs_units_ttm).toLocaleString()}</TdNum>
                        <TdNum style={{ color: (parseFloat(l.xs_cost_ttm) || 0) > 0 ? "#12b5db" : undefined }}>
                          {l.xs_cost_ttm == null ? "—" : fmt(l.xs_cost_ttm)}</TdNum>
                        {smHasT24M && <TdNum>{l.xs_units_t24m == null ? "—" : Number(l.xs_units_t24m).toLocaleString()}</TdNum>}
                        {smHasT24M && <TdNum style={{ color: (parseFloat(l.xs_cost_t24m) || 0) > 0 ? "#116588" : undefined }}>
                          {l.xs_cost_t24m == null ? "—" : fmt(l.xs_cost_t24m)}</TdNum>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination page={smPageNow} totalPages={smTotalPages}
                            total={smRows.length} pageSize={SM_PAGE} onPage={setSmPage} />
              </div>}
          </Card>
          </>}
      </div>
  );
};
